import dayjs from 'dayjs'
import type { Thunk } from 'types/redux'
import api, { statsEndpoints } from '@disco/api/server'
import {
  STATS_CHANGE_MODE,
  STATS_CHART_LOAD,
  STATS_CHART_LOAD_FAIL,
  STATS_CHART_LOAD_SUCCESS,
  STATS_LOAD,
  STATS_LOAD_FAIL,
  STATS_LOAD_SUCCESS,
  STATS_PARAMS_RESET,
  STATS_PARAMS_UPDATE,
  STATS_SUMMARY_LOAD,
  STATS_SUMMARY_LOAD_FAIL,
  STATS_SUMMARY_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import type { State } from '@disco/state-management/reducers/stats'
const DATE_FORMAT = 'YYYY-MM-DD'
const MODE_USER_TYPE = {
  team: 'staff',
  'end-user': 'client',
}
/**
 * Changes the statistics mode between team and end-user views.
 * @param mode - The mode to switch to ('team' or 'end-user')
 * @returns Action object to change the statistics mode
 */
export function changeMode(mode: keyof typeof MODE_USER_TYPE): {
  mode: keyof {
    'end-user': string
    team: string
  }
  type: string
} {
  return {
    type: STATS_CHANGE_MODE,
    mode,
  }
}
/**
 * Resets statistics parameters to their default values.
 * @returns Action object to reset statistics parameters
 */
export function resetParams(): {
  type: string
} {
  return {
    type: STATS_PARAMS_RESET,
  }
}
/**
 * Updates statistics parameters with new field values.
 * @param fields - Partial state object containing fields to update
 * @returns Action object to update statistics parameters
 */
export function updateParams(fields: Partial<State>): {
  fields: Partial<State>
  type: string
} {
  return {
    type: STATS_PARAMS_UPDATE,
    fields,
  }
}
/**
 * Refreshes all statistics data by loading summary and chart stats.
 * @returns Thunk that loads both summary and chart statistics
 */
export function refresh(): Thunk {
  return dispatch => {
    dispatch(loadSummaryStats())
    dispatch(loadChartStats())
  }
}
/**
 * Loads chart statistics for tracks and playlists.
 * @returns Thunk that loads chart data for visualization
 */
export function loadChartStats(): Thunk {
  return dispatch => {
    // total streams & individual downloads
    dispatch(loadChart('tracks'))
    // zip downloads
    dispatch(loadChart('playlists'))
  }
}
/**
 * Loads statistics data for a specific key and kind with pagination support.
 * @param key - The statistics category key
 * @param kind - The specific type of statistics within the category
 * @param loadNext - Whether to load the next page of results
 * @returns Thunk that loads the requested statistics data
 */
export function loadStats(
  key: string,
  kind: string,
  loadNext?: boolean,
): Thunk {
  return async (dispatch, getState) => {
    const {
      stats: { mode, startDate, endDate, entities },
    } = getState()
    const next = loadNext ? entities[key][kind].next : null
    const timestamp = Date.now()
    dispatch({
      type: STATS_LOAD,
      key,
      kind,
      next,
      timestamp,
    })

    try {
      if (loadNext && next) {
        const response = await api.getUrl<any>(
          next,
          null,
          'api.getNextCustomerStats',
        )
        dispatch({
          type: STATS_LOAD_SUCCESS,
          key,
          kind,
          data: [...entities[key][kind].data, ...response.results],
          next: response.next,
          timestamp,
        })
      } else {
        const response = await api.getCustomerStats(key, kind, {
          count: 20,
          filter_prop: statsEndpoints[key][kind],
          from: dayjs(startDate).format(DATE_FORMAT),
          to: dayjs(endDate).format(DATE_FORMAT),
          user_type: mode ? MODE_USER_TYPE[mode] : undefined,
        })
        dispatch({
          type: STATS_LOAD_SUCCESS,
          key,
          kind,
          data: response.results,
          next: response.next,
          timestamp,
        })
      }
    } catch (error) {
      dispatch({
        type: STATS_LOAD_FAIL,
        key,
        kind,
        error,
        timestamp,
      })
    }
  }
}
/**
 * Loads summary statistics for the current date range.
 * @returns Thunk that loads summary statistics data
 */
export function loadSummaryStats(): Thunk {
  return async (dispatch, getState) => {
    const {
      stats: { startDate, endDate },
    } = getState()
    dispatch({
      type: STATS_SUMMARY_LOAD,
    })

    try {
      const data = await api.getSummaryStats({
        from: dayjs(startDate).format(DATE_FORMAT),
        to: dayjs(endDate).format(DATE_FORMAT),
      })
      dispatch({
        type: STATS_SUMMARY_LOAD_SUCCESS,
        data,
      })
    } catch (error) {
      dispatch({
        type: STATS_SUMMARY_LOAD_FAIL,
        error,
      })
    }
  }
}

function loadChart(key: string): Thunk {
  return async (dispatch, getState) => {
    const {
      stats: { startDate, endDate },
    } = getState()
    dispatch({
      type: STATS_CHART_LOAD,
      key,
    })

    try {
      const data = await api.getChartStats(key, {
        from: dayjs(startDate).format(DATE_FORMAT),
        to: dayjs(endDate).format(DATE_FORMAT),
        user_type: 'client',
      })
      dispatch({
        type: STATS_CHART_LOAD_SUCCESS,
        key,
        data,
      })
    } catch (error) {
      dispatch({
        type: STATS_CHART_LOAD_FAIL,
        key,
        error,
      })
    }
  }
}
